// src/components/fraud/FraudRiskUsers.js
// Top Risk Users table widget.
// Columns: User | Fraud Score | High Risk Payments | Failed Payments | Status

import React from 'react';
import { Container, Table } from 'react-bootstrap';

// ─── Score badge ─────────────────────────────────────────────────────────────
const ScoreBadge = ({ score }) => {
  const color = score >= 70 ? '#EF4444' : score >= 40 ? '#F59E0B' : '#22C55E';
  const label = score >= 70 ? 'High' : score >= 40 ? 'Medium' : 'Low';
  return (
    <span className="d-flex align-items-center gap-2">
      <span style={{ fontWeight: 700, color, fontSize: 16 }}>{score}</span>
      <span
        style={{
          fontSize: 9,
          fontWeight: 700,
          color,
          background: `${color}22`,
          border: `1px solid ${color}55`,
          borderRadius: 20,
          padding: '1px 6px',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </span>
    </span>
  );
};

// ─── Status badge ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status = 'Active' }) => {
  const s = status.toLowerCase();
  const color = s === 'active' ? '#22C55E' : s === 'suspended' ? '#EF4444' : '#F59E0B';
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        color,
        background: `${color}18`,
        border: `1px solid ${color}44`,
        borderRadius: 20,
        padding: '2px 8px',
      }}
    >
      {status}
    </span>
  );
};

// ─── Skeleton rows ────────────────────────────────────────────────────────────
const SkeletonRows = () =>
  Array.from({ length: 5 }).map((_, i) => (
    <tr key={i} style={{ borderBottom: '1px solid var(--divider)' }}>
      {[180, 70, 70, 70, 70].map((w, j) => (
        <td key={j} className="transparent-header py-3">
          <div className="fraud-skeleton" style={{ width: w, height: 12, borderRadius: 4 }} />
        </td>
      ))}
    </tr>
  ));

// ─── Main Component ────────────────────────────────────────────────────────────
const FraudRiskUsers = ({ users, loading }) => (
  <div id="fraud-risk-users" className="theme-card rounded-3 p-4 h-100">
    <div className="d-flex justify-content-between align-items-center mb-4">
      <h6 className="mb-0 text-h2" style={{ fontSize: 15 }}>
        Top Risk Users
      </h6>
      <span
        style={{
          fontSize: 11,
          color: 'var(--text-secondary)',
          background: 'rgba(255,255,255,0.06)',
          borderRadius: 20,
          padding: '2px 10px',
        }}
      >
        By fraud score
      </span>
    </div>

    <div className="table-responsive">
      <Table className="align-middle body-text-two mb-0">
        <thead>
          <tr>
            {['User', 'Fraud Score', 'High Risk Pmts', 'Failed Pmts', 'Status'].map((col) => (
              <th key={col} className="transparent-header" style={{ fontSize: 12, fontWeight: 600 }}>
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <SkeletonRows />
          ) : users.length === 0 ? (
            <tr>
              <td colSpan={5} className="transparent-header text-center py-4">
                <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                  <span style={{ fontSize: 24 }}>👤</span>
                  <p className="mt-2 mb-0">No risk user data in fraud_logs</p>
                </div>
              </td>
            </tr>
          ) : (
            users.map((u, idx) => (
              <tr
                key={u.uid || idx}
                style={{ borderBottom: '1px solid var(--divider)' }}
              >
                <td className="transparent-header py-3" style={{ fontSize: 12 }}>
                  <div style={{ fontWeight: 500 }}>
                    {u.email && u.email.length > 22 ? u.email.substring(0, 22) + '…' : u.email || u.uid}
                  </div>
                  {u.email !== u.uid && (
                    <div
                      style={{
                        fontSize: 10,
                        color: 'var(--text-secondary)',
                        fontFamily: 'monospace',
                        marginTop: 2,
                      }}
                    >
                      {(u.uid || '').substring(0, 12)}…
                    </div>
                  )}
                </td>
                <td className="transparent-header py-3">
                  <ScoreBadge score={u.avgScore} />
                </td>
                <td className="transparent-header py-3" style={{ fontSize: 13, color: '#EF4444', fontWeight: 600 }}>
                  {u.highRisk}
                </td>
                <td className="transparent-header py-3" style={{ fontSize: 13, color: '#F59E0B', fontWeight: 600 }}>
                  {u.failed}
                </td>
                <td className="transparent-header py-3">
                  <StatusBadge status={u.status} />
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </div>
  </div>
);

// ─── Wrapper with Container (used directly in Dashboard layout) ───────────────
const FraudRiskUsersWidget = ({ users, loading }) => (
  <Container fluid className="px-4 my-0">
    <FraudRiskUsers users={users} loading={loading} />
  </Container>
);

export { FraudRiskUsers };
export default FraudRiskUsersWidget;
